import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { InitiationPreviewStepProps } from '../_props'
import useStyles from '../_styles'
import { ArrowForwardIcon } from 'apps/ids/src/app/assets/icons/icons'
import { EditBlackIcon } from 'apps/ids/src/app/assets/icons/icons'
import { getPatientBirthLabel, getPatientSexLabel, getCivilityLabel } from '../../../Patient/History/_utils'
import useCurrentUser from '../../../../hooks/useCurrentUser'
import { Alert, Banner } from '@workspace/frontend-kit'
import { useTransmissionLimit } from '../useTransmissionLimit'

const TRANSMISSION_ALERT_TOOLTIP_CONTENT =
    'Quota annuel pour le duo patient-requis, conformément à la limitation légale de prise en charge des téléexpertises fixée par l’Assurance Maladie, à 4 par an pour un même duo patient-requis.'

export default function InitiationPreviewStep({
    selectedPatient,
    selectedRequester,
    selectedRequired,
    selectedWorkflow,
    onEditPatient,
    onEditRequester,
    onEditRequired,
    onEditWorkflow,
    onInit,
}: InitiationPreviewStepProps) {
    const currentUser = useCurrentUser()
    const classes = useStyles()

    const { isLimitReached, isQuotaExhausted, alert } = useTransmissionLimit({
        patientId: selectedPatient?.patientId ?? '',
        requiredExpert: {
            id: selectedRequired?.requiredExpertId ?? '',
            isHcsEmployee: !!selectedRequired?.isHcsEmployee,
        },
    })

    const patientData = selectedPatient?.patientData
    const sex = patientData?.sex
    const fullName = patientData?.fullName
    const birthdate = patientData?.birthDate
    const age = patientData?.age

    const readyLabel = currentUser.sex?.toUpperCase() === 'M' ? 'Prêt' : 'Prête'

    return (
        <div style={{ width: '100%' }}>
            <Typography className={classes.title}>{readyLabel} à initier la demande ?</Typography>
            <Typography className={classes.subtitle}>
                Après cette étape, ces informations ne seront plus modifiables
            </Typography>
            <Box className={classes.section}>
                <Box className={classes.sectionContent}>
                    <Typography className={classes.sectionLabel}>Patient</Typography>
                    <Typography className={classes.sectionTitle}>
                        {`${getCivilityLabel(sex)} ${fullName || '—'}`}
                    </Typography>
                    {birthdate && (
                        <Typography className={classes.sectionSubtitle}>
                            {`${getPatientBirthLabel(sex)} le ${birthdate} (${age} ans) - Sexe ${getPatientSexLabel(
                                sex,
                            )}`}
                        </Typography>
                    )}
                </Box>
                <Button onClick={onEditPatient} className={classes.editButton}>
                    <EditBlackIcon />
                </Button>
            </Box>
            <Box className={classes.section}>
                <Box className={classes.sectionContent}>
                    <Typography className={classes.sectionLabel}>Requérant</Typography>
                    <Typography className={classes.sectionTitle}>
                        {selectedRequester?.requiredExpertLabel || '—'}
                    </Typography>
                    <Typography className={classes.sectionSubtitle}>
                        {selectedRequester?.profession && selectedRequester?.speciality
                            ? `${selectedRequester.profession} - ${selectedRequester.speciality}`
                            : selectedRequester?.profession || selectedRequester?.speciality || ''}
                    </Typography>
                </Box>
                <Button onClick={onEditRequester} className={classes.editButton}>
                    <EditBlackIcon />
                </Button>
            </Box>
            <Box className={classes.section}>
                <Box className={classes.sectionContent}>
                    <Typography className={classes.sectionLabel}>Requis</Typography>
                    <Typography className={classes.sectionTitle}>
                        {selectedRequired?.requiredExpertLabel || '—'}
                    </Typography>
                    <Typography className={classes.sectionSubtitle}>
                        {selectedRequired?.profession && selectedRequired?.speciality
                            ? `${selectedRequired.profession} - ${selectedRequired.speciality}`
                            : selectedRequired?.profession || selectedRequired?.speciality || ''}
                    </Typography>
                </Box>

                {alert?.title && (
                    <Box sx={{ flexShrink: 0, maxWidth: '45%', marginRight: '1rem' }}>
                        <Alert
                            title={alert.title}
                            text={alert.text}
                            tooltip={TRANSMISSION_ALERT_TOOLTIP_CONTENT}
                            severity={alert.severity}
                        />
                    </Box>
                )}
                <Button onClick={onEditRequired} className={classes.editButton}>
                    <EditBlackIcon />
                </Button>
            </Box>
            <Box className={classes.section}>
                <Box className={classes.sectionContent}>
                    <Typography className={classes.sectionLabel}>Cible</Typography>
                    <Typography className={classes.sectionTitle}>{selectedWorkflow?.name || '—'}</Typography>
                </Box>
                <Button onClick={onEditWorkflow} className={classes.editButton}>
                    <EditBlackIcon />
                </Button>
            </Box>

            {isLimitReached && (
                <Banner
                    severity="WARNING"
                    title="Limite annuelle atteinte pour le duo patient-requis :"
                    text="Veuillez contacter le secrétariat du médecin pour convenir d’une modalité de prise en charge du patient"
                />
            )}
            {isQuotaExhausted && (
                <Banner
                    severity="NEUTRAL"
                    title="Limite annuelle atteinte pour le duo patient-requis :"
                    text="Le requis ne pourra pas facturer cette téléexpertise à l’Assurance Maladie et peut la refuser"
                />
            )}
            <Button
                onClick={onInit}
                sx={{
                    marginTop: '24px',
                    marginBottom: '40px',
                    width: '100%',
                    borderRadius: '8px',
                    minWidth: '168px',
                    backgroundColor: '#575BAF',
                    color: '#FFF',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '18px 32px',
                    fontFamily: 'AvertaPE-Semibold',
                    fontSize: '16px',
                    fontWeight: 600,
                    lineHeight: '24px',
                    '&:hover': {
                        backgroundColor: '#575BAF',
                    },
                    '&:disabled': {
                        backgroundColor: '#E0E0E0',
                        color: '#8B8B8B',
                        '& svg path': {
                            fill: '#8B8B8B',
                        },
                    },
                }}
                disabled={isLimitReached}
            >
                Initier
                <ArrowForwardIcon />
            </Button>
        </div>
    )
}
